import React from 'react';
import axios from 'axios';
import Alert from '@material-ui/lab/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import List, { ListProps } from '@material-ui/core/List';
import ListItem, { ListItemProps } from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction, { ListItemSecondaryActionProps } from '@material-ui/core/ListItemSecondaryAction';
import Collapse from '@material-ui/core/Collapse';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import Icon, { IconProps } from '@material-ui/core/Icon';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Typography, { TypographyProps } from '@material-ui/core/Typography';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import {
  IProductsServicesProps,
  IProduct,
  IProductsServicesState,
  IProductToSubmit
} from '../../types/ProductsServices.types';

import { endpoints } from '../../config';

const useStyles = makeStyles(theme =>
  createStyles({
    list: {
      height: 320,
      width: '100%',
      overflow: 'auto'
    },
    listItem: {
      borderBottom: '1px solid #ededed',
      cursor: 'pointer'
    },
    icons: {
      cursor: 'pointer'
    },
    textField: {
      borderRadius: 7,
      marginTop: 30,
      '& .MuiIcon-root': {
        color: '#a2a2a2'
      },
      '&.MuiTextField-root, &.MuiInput-root': {
        width: '100%'
      }
    },
    typography: {
      marginTop: 20
    }
  })
);

export const ListStyled: React.FC<ListProps> = (props: ListProps) => {
  const classes = useStyles();
  return (
    <List {...props} className={classes.list}>
      {props.children}
    </List>
  );
};

export const IconStyled: React.FC<IconProps> = (props: IconProps) => {
  const classes = useStyles();
  return (
    <Icon {...props} className={classes.icons}>
      {props.children}
    </Icon>
  );
};

export const ListItemSecondaryActionStyled: React.FC<ListItemSecondaryActionProps> = (
  props: ListItemSecondaryActionProps
) => {
  const classes = useStyles();
  return (
    <ListItemSecondaryAction {...props} className={classes.icons}>
      {props.children}
    </ListItemSecondaryAction>
  );
};

export const ListItemStyled: React.FC<ListItemProps> = (props: ListItemProps) => {
  const classes = useStyles();
  const { button, ...restProps } = props;
  return (
    <ListItem {...restProps} className={classes.listItem}>
      {props.children}
    </ListItem>
  );
};

export const TextFieldStyled: React.FC<TextFieldProps> = (props: TextFieldProps) => {
  const classes = useStyles();
  return (
    <TextField {...props} className={classes.textField}>
      {props.children}
    </TextField>
  );
};

export const TypographyStyled: React.FC<TypographyProps> = (props: TypographyProps) => {
  const classes = useStyles();
  return (
    <Typography {...props} className={classes.typography}>
      {props.children}
    </Typography>
  );
};

class ProductsServices extends React.Component<IProductsServicesProps, IProductsServicesState> {
  state = {
    productsList: [],
    productsListFiltered: [],
    addedProducts: [],
    addedProductsFiltered: [],
    searchText: '',
    loading: false
  };

  componentDidMount = async () => {
    if (this.props.initialState.productsAddedList.length > 0) {
      const addedProducts: IProduct[] = this.props.initialState.productsAddedList.map(
        (product: IProductToSubmit): IProduct => {
          return {
            checked: false,
            itemKey: product?.itemKey,
            description: product?.nameToAdd || '',
            nameToAdd: product?.nameToAdd,
            child: [],
            isExpanded: false
          };
        }
      );
      this.setState({
        addedProducts,
        addedProductsFiltered: addedProducts,
        productsListFiltered: this.props.initialState.productsList as IProduct[]
      });
    }

    if (typeof this.props.cachedResponse !== 'undefined') {
      this.processProductsServicesList(this.props.cachedResponse);
    } else {
      this.setState({ loading: true });
      await this.getProductsList();
      this.setState({ loading: false });
    }
  };

  getProductsList = async () => {
    try {
      const list = await axios.get(`${endpoints.Products}`);
      this.processProductsServicesList(list);
    } catch (error) {
      console.error('Error requesting products list', error);
    }
  };

  processProductsServicesList = (list: any) => {
    const stateList = this.stateBuilder(list.data);
    this.setState(
      {
        productsList: stateList,
        productsListFiltered:
          this.props.initialState.productsAddedList.length > 0 && !this.props.isAlreadyTagged
            ? (this.props.initialState.productsList as IProduct[])
            : stateList
      },
      () => {
        for (const item of this.props.initialState.productsAddedList) {
          this.findElementToRemoveAdd(this.state.productsListFiltered, item, true);
        }
      }
    );
  };

  stateBuilder = (list: IProduct[], nameToAdd?: string) => {
    return list.map((element: IProduct) => {
      const product = {
        ...element,
        nameToAdd: nameToAdd ? `${nameToAdd} > ${element.description}` : `${element.description}`,
        isExpanded: false,
        checked: false
      };
      if (element.child && element.child?.length > 0) {
        const stringToAdd = nameToAdd ? `${nameToAdd} > ${element.description}` : `${element.description}`;
        product.child = this.stateBuilder(element.child, stringToAdd);
      }
      return product;
    });
  };

  makeCollapseVisible(item: IProduct) {
    item.isExpanded = !item.isExpanded;
    this.setState({});
  }

  renderList = (list: IProduct[], level: number) => {
    if (this.state.loading) {
      return (
        <div style={{ overflow: 'hidden' }}>
          <CircularProgress style={{ width: '24px', height: '24px' }} />
        </div>
      );
    } else {
      const renderList = list.map((element: IProduct, index: number) => (
        <>
          <ListItemStyled
            key={element.itemKey}
            onClick={() => this.makeCollapseVisible(element)}
            style={{ paddingLeft: `calc(${level} * 30px` }}
            disabled={!element.child && element.checked}
          >
            {element.child && element.child.length > 0 ? (
              <ListItemIcon>
                <i
                  className="material-icons"
                  title={
                    element.isExpanded
                      ? this.props.languageStrings.expandIconTooltip
                      : this.props.languageStrings.collapseIconTooltip
                  }
                >
                  {element.isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
                </i>
              </ListItemIcon>
            ) : (
              <ListItemIcon></ListItemIcon>
            )}
            <ListItemText primary={element.description} />
            {(element.child && element.child[0].child && element.child[0].child.length > 0) || element.checked || (
              <ListItemSecondaryActionStyled onClick={() => this.addProductToList(element)}>
                <i className="material-icons" title={this.props.languageStrings.productsServices.addProductTooltip}>
                  add
                </i>
              </ListItemSecondaryActionStyled>
            )}
          </ListItemStyled>
          {element.isExpanded && element.child && element.child.length > 0 && (
            <Collapse key={index} in={element.isExpanded}>
              {this.renderList(element.child, level + 1)}
            </Collapse>
          )}
        </>
      ));
      return renderList;
    }
  };

  addProductToList = (item: IProduct) => {
    if (!item.checked) {
      if (item.child && item.child.length > 0) {
        item.isExpanded = false;
        const filteredChild = item.child.filter((child: IProduct) => child.child);
        if (filteredChild && filteredChild.length > 0) {
          this.setState({});
        } else {
          const filteredChecked = item.child.filter((child: IProduct) => child.checked);
          if (filteredChecked?.length > 0) {
            this.setState({});
          } else {
            for (const child of item.child) {
              child.checked = true;
            }
            item.checked = true;
            this.findElementToRemoveAdd(this.state.productsList, item, true);
            this.setState({
              addedProducts: [...this.state.addedProducts, ...item.child],
              addedProductsFiltered: [...this.state.addedProductsFiltered, ...item.child]
            });
          }
        }
      } else {
        this.findElementToRemoveAdd(this.state.productsList, item, true);
        item.checked = true;
        this.setState({
          addedProducts: [...this.state.addedProducts, item],
          addedProductsFiltered: [...this.state.addedProductsFiltered, item]
        });
      }
    }
  };

  findElementToRemoveAdd = (list: IProduct[], item: IProduct, add?: boolean) => {
    for (const product of list) {
      const filteredChild = product.child?.filter((child: IProduct) => child.child);
      if (filteredChild && filteredChild.length > 0) {
        this.findElementToRemoveAdd(product.child as IProduct[], item, add);
      } else {
        if (product.child && product.child.length > 0) {
          product.child?.forEach((element: IProduct) => {
            if (element.itemKey === item.itemKey) {
              element.checked = add;
            }
          });
          const filteredChecked = product.child?.filter(element => element.checked);
          product.checked = filteredChecked && filteredChecked.length > 0;
        } else {
          if (product.itemKey === item.itemKey) {
            product.checked = add;
          }
        }
      }
    }
  };

  removeProductFromList = (index: number, added: IProduct) => {
    this.findElementToRemoveAdd(this.state.productsListFiltered, added, false);
    this.findElementToRemoveAdd(this.state.productsList, added, false);
    this.setState(({ addedProducts, addedProductsFiltered }) => ({
      addedProducts: [...addedProducts.slice(0, index), ...addedProducts.slice(index + 1)],
      addedProductsFiltered: [...addedProductsFiltered.slice(0, index), ...addedProductsFiltered.slice(index + 1)]
    }));
  };

  recursiveSearch = (list: IProduct[], value: string, filtered: IProduct[] = []) => {
    if (list.length > 0) {
      for (const product of list) {
        const prodToPass = { ...product };
        const itemDidMatch = prodToPass.nameToAdd?.toUpperCase().includes(value.toUpperCase());
        itemDidMatch && filtered.push(prodToPass);
        prodToPass.isExpanded = itemDidMatch && prodToPass.child && prodToPass.child.length > 0;
        prodToPass.child && !itemDidMatch && this.recursiveSearch(prodToPass.child, value, filtered);
      }
    }
  };

  searchFilter = (value: string) => {
    let filteredAvailableList: IProduct[] = [];
    let filteredSelectedList: IProduct[] = [];

    this.recursiveSearch(this.state.addedProducts, value, filteredSelectedList);
    this.recursiveSearch(this.state.productsList, value, filteredAvailableList);

    this.setState({
      searchText: value,
      productsListFiltered:
        value === '' || filteredAvailableList.length > 60 ? this.state.productsList : filteredAvailableList,
      addedProductsFiltered:
        value === '' || filteredSelectedList.length > 60 ? this.state.addedProducts : filteredSelectedList
    });
  };

  resetSearch = () => {
    for (const added of this.state.addedProducts) {
      this.findElementToRemoveAdd(this.state.productsList, added, true);
    }
    this.setState({
      searchText: '',
      productsListFiltered: this.state.productsList,
      addedProductsFiltered: this.state.addedProducts
    });
  };

  nextStep = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const mappedArray: IProductToSubmit[] = this.state.addedProducts.map((product: IProduct) => ({
      itemKey: product.itemKey,
      nameToAdd: product.nameToAdd
    }));
    let eventMapped = { target: { value: mappedArray } };
    this.props.handleChange &&
      this.props.handleChange('productsServices', 'productsAddedList', () => {
        // Next step triggered in a callback in case the diversity is the last step of the registration process.
        eventMapped = { target: { value: this.state.productsListFiltered } };
        this.state.addedProducts.length > 0 &&
          this.props.handleChange &&
          this.props.handleChange('productsServices', 'productsList')(eventMapped);
        this.props.nextStep();
      })(eventMapped);
  };

  previousStep = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const mappedArray: IProductToSubmit[] = this.state.addedProducts.map((product: IProduct) => ({
      itemKey: product.itemKey,
      nameToAdd: product.nameToAdd
    }));
    let eventMapped = { target: { value: mappedArray } };
    this.props.handleChange && this.props.handleChange('productsServices', 'productsAddedList')(eventMapped);
    eventMapped = { target: { value: this.state.productsListFiltered } };
    this.state.addedProducts.length > 0 &&
      this.props.handleChange &&
      this.props.handleChange('productsServices', 'productsList')(eventMapped);
    this.props.previousStep();
  };

  renderTaggedMessage(isAlreadyTagged: boolean = false, taggedDate: string = '', productsAddedList: IProduct[]) {
    if (isAlreadyTagged && taggedDate.trim().length > 0 && productsAddedList.length > 0) {
      return (
        <div className="taggedMessage">
          <Alert severity="info">
            {this.props.languageStrings.formatString(this.props.languageStrings.common.tagInstructionPrimary, {
              taggedDate
            })}
            .&nbsp;
            <strong>
              {this.props.languageStrings.common.tagInstructionSecondary}&nbsp;
              <span>{this.props.languageStrings.continueButtonLabel}.</span>
            </strong>
          </Alert>
        </div>
      );
    } else {
      return '';
    }
  }

  render() {
    return (
      <Container style={{ height: 710 }}>
        <Grid container justify="center" spacing={1}>
          {this.renderTaggedMessage(
            this.props.isAlreadyTagged,
            this.props.taggedDate,
            this.props.initialState.productsAddedList
          )}
          <Grid container justify="center" item md={12}>
            <Grid item md={4}>
              <TextFieldStyled
                type="text"
                id="search"
                variant="outlined"
                placeholder={this.props.languageStrings.productsServices.searchPlaceholder}
                value={this.state.searchText}
                onChange={(event: any) => {
                  this.searchFilter(event.target.value);
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconStyled>
                        <i
                          className="material-icons"
                          title={this.props.languageStrings.productsServices.cancelIconTooltip}
                          onClick={() => this.resetSearch()}
                        >
                          cancel
                        </i>
                      </IconStyled>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
          <Grid container item md={5} justify="center">
            <TypographyStyled variant="h6">Available Products & Services</TypographyStyled>
            <div style={{ border: '1px solid black', width: '100%', padding: 20 }}>
              <ListStyled>{this.renderList(this.state.productsListFiltered, 0)}</ListStyled>
            </div>
          </Grid>
          <Grid container item md={5} justify="center" alignContent="flex-start">
            <TypographyStyled variant="h6">
              {this.props.languageStrings.productsServices.selectedProductsListTitle}
            </TypographyStyled>
            <div style={{ border: '1px solid black', width: '100%', padding: 20 }}>
              <ListStyled>
                {this.state.addedProductsFiltered.map((added: IProduct, key: number) => (
                  <ListItemStyled key={added.description}>
                    <ListItemText primary={added.nameToAdd} />
                    <ListItemIcon onClick={() => this.removeProductFromList(key, added)}>
                      <i className="material-icons" title={this.props.languageStrings.productsServices.removeProduct}>
                        cancel
                      </i>
                    </ListItemIcon>
                  </ListItemStyled>
                ))}
              </ListStyled>
            </div>
          </Grid>
          <div className="Form-actions multiple">
            <Button
              variant="outlined"
              color="primary"
              onClick={e => {
                this.previousStep(e);
              }}
            >
              {this.props.languageStrings.backButtonLabel}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={e => {
                this.nextStep(e);
              }}
              disabled={this.state.addedProducts?.length <= 0}
            >
              {this.props.languageStrings.continueButtonLabel}
            </Button>
          </div>
        </Grid>
      </Container>
    );
  }
}

export default ProductsServices;
